import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminHeaders } from '../../lib/adminAuth';
import { useAdminI18n, tx } from '../../lib/adminI18n';
import './OtaReviewPage.css';

// ============================================================
// OTA同期の確認  /admin/ota-review
//   API: /api/admin/ota-review/*（routes/admin/ota-review.ts）
//   タブ: 補完待ち / ブロック確認 / キャンセル疑い・重複 / UID変更の疑い / 日程変更
//   キャンセル疑い（cancel_suspicions）は iCal 同期の中で判定され、「キャンセル疑い・重複」に表示する
// ============================================================

type Tab = 'incomplete' | 'blocks' | 'review' | 'pending' | 'dates';
type Lang = 'ja' | 'zh' | 'en';

const L: Record<Lang, Record<string, string>> = {
  ja: {
    allProps: 'すべての物件', syncing: 'iCal取得中…', syncReload: 'iCalから再取得', syncFail: 'iCalの取得に失敗しました', scopeAll: 'すべての物件の確認情報です（毎時の同期で取得済みのデータ）。物件ボタンで絞り込めます。', scopeOne: 'この物件の確認情報です（同期済みのデータ）。最新にするには「この物件を今すぐ同期」を押してください。',
    title: 'OTA同期の確認', incomplete: '予約情報補完待ち', review: 'キャンセル疑い・重複', pending: 'UID変更の疑い', dates: '日程変更',
    empty: '対象の予約はありません', property: '施設', ota: '予約元', stay: '宿泊日', guest: '予約者', count: '人数', phone: '電話',
    email: 'メール', code: 'OTA予約番号', save: '保存', saved: '保存しました', reason: '理由', MISSING: 'OTAのカレンダーから消えています',
    OVERLAP: '他の予約と日付が重なっています', overlaps: '重なっている予約', missingSince: '消えた日時', cancel: 'キャンセル確定', keep: '問題なし',
    confirmCancel: 'OTA側でキャンセルを確認しましたか？この予約をキャンセルにし、清掃予定も取り消します。', newEvent: '新しいイベント',
    candidate: '同じ予約と思われる既存予約', merge: '同じ予約（既存予約を更新）', create: '別の新規予約として登録', ignore: '無視',
    before: '変更前', after: '変更後', ack: '確認済み', history: '履歴', close: '閉じる', error: 'エラーが発生しました', reload: '再読み込み',
    unset: '未入力', persons: '名',
    blocks: 'ブロック確認', blockLead: 'Airbnbで「予約不可」になっている期間です。他のOTA（Booking.comなど）の予約か、本当のブロックかを確認してください。在庫は確認前から売り止めになっています。',
    cancelTab: 'キャンセル検知', cancelLead: 'OTAのカレンダー（iCal）でこの予約の日程が見つからなくなった予約です。OTAの管理画面で状態を確認し、キャンセル確定か「予約は有効」を選んでください。',
    rescan: 'キャンセル検知を再判定', rescanning: '再判定中…', rescanDone: '再判定しました', confidence: '確度', HIGH: '高', MEDIUM: '中', LOW: '低',
    detectedAt: '検知', streak: '連続未検出', times: '回', feed: '照合したフィード', uid: 'UID', noUid: 'UIDなし（CSV・手入力）', overlapEv: 'フィード上の重なるイベント', none: 'なし',
    valid: '予約は有効', confirmValid: 'この予約は有効として扱いますか？同じ日程のままなら再検知しません。',
    R_UID_MISSING: 'OTAのカレンダーからこの予約のイベントが消えました', R_NO_COVERAGE: 'OTAのカレンダーにこの日程のイベントがありません',
    R_PARTIAL_COVERAGE: 'OTAのカレンダーの日程と一部しか一致しません（日程変更の可能性）', R_SOURCE_BLOCK_REMOVED: '登録元のAirbnbブロックが消えました', R_FEED_EMPTIED: 'OTAのカレンダーに今後のイベントが1件もありません', R_DATE_MISMATCH: 'OTAのカレンダーと日程が違います（日程変更の可能性）', R_ACTIVE_IN_FEED: 'キャンセル済みですが、OTAでは有効な予約です', restore: '予約を復活', confirmRestore: 'OTAの管理画面で予約が有効なことを確認しましたか？予約を「予約確定」に戻し、在庫ブロックと清掃予定を作り直します。', notRestore: 'キャンセルのまま', confirmNotRestore: 'キャンセルのままにしますか？同じ日程のままなら再検知しません。', feedActive: 'OTAで有効', feedMissing: 'OTAにない', feedUnknown: 'UIDなし', liveWarn: 'この予約はOTAのカレンダーに有効な予約として残っています。本当にキャンセルしますか？', overlapActive: '日程が重なる有効な予約', feedDates: 'OTA上の日程', DATE_MISMATCH: 'OTAのカレンダーと日程が違います',
    nights: '泊', isReservation: '他OTAの予約として登録', channel: '予約元', isBlock: '本当のブロック（予約なし）', sameAs: 'この予約と同じ', overlapRes: '日程が重なる予約', confirmBlock: '本当のブロックとして確定しますか？',
  },
  zh: {
    allProps: '全部房源', syncing: '正在获取iCal…', syncReload: '从iCal重新获取', syncFail: 'iCal获取失败', scopeAll: '全部房源的确认信息（每小时同步获取的数据）。可用房源按钮筛选。', scopeOne: '该房源的确认信息（已同步的数据）。如需最新数据请点击「立即同步此房源」。',
    title: 'OTA同步确认', incomplete: '待补充预订信息', review: '疑似取消・重复', pending: '疑似UID变更', dates: '日期变更',
    empty: '没有需要处理的预订', property: '房源', ota: '渠道', stay: '入住日期', guest: '预订人', count: '人数', phone: '电话',
    email: '邮箱', code: 'OTA预订号', save: '保存', saved: '已保存', reason: '原因', MISSING: '已从OTA日历中消失',
    OVERLAP: '与其他预订日期重叠', overlaps: '重叠的预订', missingSince: '消失时间', cancel: '确认取消', keep: '没有问题',
    confirmCancel: '已在OTA确认取消了吗？将取消此预订并取消清扫计划。', newEvent: '新事件',
    candidate: '疑似同一预订的现有预订', merge: '同一预订（更新现有预订）', create: '作为新预订登记', ignore: '忽略',
    before: '变更前', after: '变更后', ack: '已确认', history: '记录', close: '关闭', error: '发生错误', reload: '刷新',
    unset: '未填写', persons: '人',
    blocks: '封锁确认', blockLead: '这是Airbnb上设为“不可预订”的期间。请确认是其他OTA（Booking.com等）的预订，还是真正的封锁。确认前库存已停止销售。',
    cancelTab: '取消检测', cancelLead: '在OTA日历（iCal）中已找不到该预订日期的预订。请在OTA后台确认状态，然后选择确认取消或“预订有效”。',
    rescan: '重新检测取消', rescanning: '检测中…', rescanDone: '已重新检测', confidence: '可信度', HIGH: '高', MEDIUM: '中', LOW: '低',
    detectedAt: '检测', streak: '连续未检测到', times: '次', feed: '比对的日历', uid: 'UID', noUid: '无UID（CSV・手动）', overlapEv: '日历中重叠的事件', none: '无',
    valid: '预订有效', confirmValid: '将此预订视为有效吗？日期不变时不会再次检测。',
    R_UID_MISSING: '该预订的事件已从OTA日历中消失', R_NO_COVERAGE: 'OTA日历中没有该日期的事件',
    R_PARTIAL_COVERAGE: '与OTA日历的日期仅部分一致（可能已改期）', R_SOURCE_BLOCK_REMOVED: '来源的Airbnb封锁已消失', R_FEED_EMPTIED: 'OTA日历中没有任何今后的事件', R_DATE_MISMATCH: '与OTA日历的日期不一致（可能已改期）', R_ACTIVE_IN_FEED: '已取消，但在OTA上仍是有效预订', restore: '恢复预订', confirmRestore: '已在OTA后台确认该预订有效了吗？将恢复为“已确认”，并重建库存封锁和清扫计划。', notRestore: '保持取消', confirmNotRestore: '保持取消吗？日期不变时不会再次检测。', feedActive: 'OTA上有效', feedMissing: 'OTA上没有', feedUnknown: '无UID', liveWarn: '该预订在OTA日历中仍是有效预订。确定要取消吗？', overlapActive: '日期重叠的有效预订', feedDates: 'OTA上的日期', DATE_MISMATCH: '与OTA日历的日期不一致',
    nights: '晚', isReservation: '登记为其他OTA的预订', channel: '渠道', isBlock: '真正的封锁（无预订）', sameAs: '与此预订相同', overlapRes: '日期重叠的预订', confirmBlock: '确定为真正的封锁吗？',
  },
  en: {
    allProps: 'All properties', syncing: 'Fetching iCal…', syncReload: 'Re-fetch iCal', syncFail: 'Failed to fetch iCal', scopeAll: 'Review items for all properties (data from the hourly sync). Use the property buttons to filter.', scopeOne: 'Review items for this property (synced data). Press "Sync this property now" to refresh from iCal.',
    title: 'OTA sync review', incomplete: 'Guest info needed', review: 'Possible cancel / overlap', pending: 'Possible UID change', dates: 'Date changes',
    empty: 'Nothing to review', property: 'Property', ota: 'Channel', stay: 'Stay', guest: 'Guest', count: 'Guests', phone: 'Phone',
    email: 'Email', code: 'OTA code', save: 'Save', saved: 'Saved', reason: 'Reason', MISSING: 'Removed from the OTA calendar',
    OVERLAP: 'Overlaps another reservation', overlaps: 'Overlapping', missingSince: 'Missing since', cancel: 'Confirm cancel', keep: 'Keep',
    confirmCancel: 'Have you confirmed the cancellation on the OTA? This cancels the reservation and its cleaning task.', newEvent: 'New event',
    candidate: 'Likely the same reservation', merge: 'Same reservation (update existing)', create: 'Register as new', ignore: 'Ignore',
    before: 'Before', after: 'After', ack: 'Acknowledge', history: 'History', close: 'Close', error: 'An error occurred', reload: 'Reload',
    unset: 'Not set', persons: '',
    blocks: 'Block check', blockLead: 'These dates are blocked on Airbnb. Check whether each is a reservation from another OTA (e.g. Booking.com) or a real block. Inventory is already closed.',
    cancelTab: 'Cancel detection', cancelLead: 'The dates of these reservations can no longer be found in the OTA calendar (iCal). Check the status on the OTA, then confirm the cancellation or mark the reservation as valid.',
    rescan: 'Re-run cancel detection', rescanning: 'Checking…', rescanDone: 'Re-checked', confidence: 'Confidence', HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low',
    detectedAt: 'Detected', streak: 'Missed', times: 'x', feed: 'Feed', uid: 'UID', noUid: 'No UID (CSV / manual)', overlapEv: 'Overlapping feed events', none: 'None',
    valid: 'Reservation is valid', confirmValid: 'Treat this reservation as valid? It will not be flagged again unless its dates change.',
    R_UID_MISSING: 'The event for this reservation was removed from the OTA calendar', R_NO_COVERAGE: 'No event for these dates in the OTA calendar',
    R_PARTIAL_COVERAGE: 'Only partly matches the OTA calendar (possible date change)', R_SOURCE_BLOCK_REMOVED: 'The source Airbnb block was removed', R_FEED_EMPTIED: 'The OTA calendar has no upcoming events', R_DATE_MISMATCH: 'Dates differ from the OTA calendar (possible date change)', R_ACTIVE_IN_FEED: 'Cancelled here, but still active on the OTA', restore: 'Restore reservation', confirmRestore: 'Have you confirmed on the OTA that this reservation is active? It will be set back to confirmed, and its inventory block and cleaning task will be recreated.', notRestore: 'Keep cancelled', confirmNotRestore: 'Keep this reservation cancelled? It will not be flagged again unless its dates change.', feedActive: 'Active on OTA', feedMissing: 'Not on OTA', feedUnknown: 'No UID', liveWarn: 'This reservation is still active in the OTA calendar. Cancel it anyway?', overlapActive: 'Overlapping active reservations', feedDates: 'Dates on the OTA', DATE_MISMATCH: 'Dates differ from the OTA calendar',
    nights: ' nights', isReservation: 'Register as another OTA reservation', channel: 'Channel', isBlock: 'Real block (no reservation)', sameAs: 'Same as this reservation', overlapRes: 'Overlapping reservations', confirmBlock: 'Confirm as a real block?',
  },
};

const OTA_LABEL: Record<string, string> = { airbnb: 'Airbnb', booking: 'Booking.com', direct: '自社サイト' };

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/admin/ota-review${path}`, {
    credentials: 'include',
    ...init,
    headers: { ...adminHeaders(), 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
  return data as T;
}

const md = (d?: string | null) => (d ? d.slice(5).replace('-', '/') : '-');
const stay = (a?: string | null, b?: string | null) => `${md(a)} → ${md(b)}`;

export default function OtaReviewPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { lang } = useAdminI18n();
  const s = L[(lang as Lang) in L ? (lang as Lang) : 'ja'];
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'incomplete';
  // 物件の絞り込み（?property=ID。'' = すべての物件）
  const propertyId = params.get('property') || '';
  const [props, setProps] = useState<{ id: string; name: string; abbreviation?: string | null; ical_configured: number; total: number }[]>([]);
  const [syncing, setSyncing] = useState(false);
  const setProperty = (id: string) => {
    const n = new URLSearchParams(params);
    if (id) n.set('property', id); else n.delete('property');
    setParams(n, { replace: true });
  };
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [history, setHistory] = useState<{ id: string; items: any[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setMsg('');
    try {
      const path = { incomplete: '/incomplete', blocks: '/blocks', review: '/review', pending: '/pending', dates: '/date-changes' }[tab];
      const q = propertyId ? `property_id=${encodeURIComponent(propertyId)}` : '';
      const withQ = (u: string) => (q ? `${u}${u.includes('?') ? '&' : '?'}${q}` : u);
      const [sum, list, byProp] = await Promise.all([
        api(withQ('/summary')), api<{ items: any[] }>(withQ(path)), api<{ items: any[] }>('/summary-by-property'),
      ]);
      setCounts(sum); setItems(list.items || []); setProps(byProp.items || []);
    } catch (e: any) { setMsg(`${s.error}: ${e.message}`); }
    finally { setLoading(false); }
  }, [tab, propertyId, s.error]);

  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<any>, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await fn(); await load(); window.dispatchEvent(new Event('ota-review-changed')); }
    catch (e: any) { setMsg(`${s.error}: ${e.message}`); }
  };

  // 物件ボタン: 同期済みデータの絞り込みのみ（iCalは取得しない）
  const selectProperty = (id: string) => setProperty(id);
  // 「この物件を今すぐ同期」ボタン: 選択中物件の iCal（Airbnb / Booking.com）を取得 → 一覧を再読み込み
  const syncNow = async (id: string) => {
    if (!id) return;
    const p = props.find((x) => x.id === id);
    if (p && !Number(p.ical_configured)) return;
    setSyncing(true); setMsg('');
    try {
      const res = await fetch(`/api/admin/ical/sync/${encodeURIComponent(id)}`, { method: 'POST', headers: adminHeaders(), credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) setMsg(`${s.syncFail}: ${d.error || res.status}`);
      window.dispatchEvent(new Event('ota-review-changed'));
    } catch (e: any) { setMsg(`${s.syncFail}: ${e.message}`); }
    finally { setSyncing(false); await load(); }
  };
  const propLabel = (p: { name: string; abbreviation?: string | null }) => (p.abbreviation || '').trim() || p.name;

  const openHistory = async (id: string) => {
    try { const r = await api<{ items: any[] }>(`/reservations/${id}/history`); setHistory({ id, items: r.items }); }
    catch (e: any) { setMsg(`${s.error}: ${e.message}`); }
  };

  const tabs: { key: Tab; label: string; n: number }[] = [
    { key: 'incomplete', label: s.incomplete, n: counts.incomplete || 0 },
    { key: 'blocks', label: s.blocks, n: counts.blocks || 0 },
    { key: 'review', label: s.review, n: counts.review || 0 },
    { key: 'pending', label: s.pending, n: counts.pending || 0 },
    { key: 'dates', label: s.dates, n: counts.date_changed || 0 },
  ];

  return (
    <div className="ota-review">
      <div className="ota-head">
        {embedded ? null : <h1>{s.title}</h1>}
        <div className="ota-props">
          <button type="button" className={`ota-prop ${propertyId === '' ? 'active' : ''}`} onClick={() => selectProperty('')}>
            {s.allProps}
          </button>
          {props.map((p) => (
            <button key={p.id} type="button" title={p.name}
              className={`ota-prop ${propertyId === p.id ? 'active' : ''}`} onClick={() => selectProperty(p.id)}>
              {propLabel(p)}{Number(p.total) > 0 && <span className="ota-badge">{p.total}</span>}
            </button>
          ))}
        </div>
        {/* カレンダー画面と同じ「今すぐ同期」ボタン。すべての物件選択時は無効（物件を選ぶと有効） */}
        <button type="button" className="ota-btn sync-now-btn" disabled={syncing || !propertyId}
          title={!propertyId ? tx('cal.8') : tx('cal.9')}
          onClick={() => void syncNow(propertyId)}>
          {syncing ? tx('cal.10') : tx('cal.11')}
        </button>
      </div>
      <div className="ota-sub ota-scope">{propertyId ? s.scopeOne : s.scopeAll}</div>

      <div className="ota-tabs">
        {tabs.map((t) => (
          <button key={t.key} type="button" className={`ota-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => { const n = new URLSearchParams(params); n.set('tab', t.key); setParams(n, { replace: true }); }}>
            {t.label}{t.n > 0 && <span className="ota-badge">{t.n}</span>}
          </button>
        ))}
      </div>

      {msg && <div className="ota-msg">{msg}</div>}
      {loading ? <div className="ota-empty">…</div> : items.length === 0 ? <div className="ota-empty">{s.empty}</div> : (
        <div className="ota-list">
          {tab === 'incomplete' && items.map((r) => <GuestForm key={r.id} r={r} s={s} onSaved={load} onHistory={openHistory} />)}

          {tab === 'review' && items.filter((r) => r.cs_id).map((r) => (
            <CancelCard key={r.id} s={s} run={run} onHistory={openHistory} x={{
              id: r.cs_id, reservation_id: r.id, property_name: r.property_name, channel: r.source_channel,
              checkin_date: r.checkin_date, checkout_date: r.checkout_date, guest_name: r.guest_name, guest_count: r.guest_count,
              ota_reservation_code: r.ota_reservation_code, ota_event_uid: r.ota_event_uid,
              rule: r.cs_rule, confidence: r.cs_confidence, streak: r.cs_streak, reservation_status: r.reservation_status,
              opened_at: r.cs_opened_at, detected_at: r.cs_detected_at, evidence: r.cs_evidence,
            }} />
          ))}
          {tab === 'review' && items.filter((r) => !r.cs_id).map((r) => (
            <div key={r.id} className={`ota-card ${r.review_reason === 'OVERLAP' ? 'warn' : 'danger'}`}>
              <CardHead r={r} s={s} onHistory={openHistory} />
              {r.review_reason === 'OVERLAP' && r.ota_event_uid && <div className="ota-row"><FeedState v={r.in_feed ? 'active' : 'missing'} s={s} /></div>}
              <div className="ota-row"><b>{s.reason}:</b> {s[r.review_reason] || r.review_reason}
                {r.missing_since && <span className="ota-sub">（{s.missingSince}: {r.missing_since}）</span>}</div>
              {r.overlaps?.length > 0 && (
                <div className="ota-row"><b>{s.overlaps}:</b>
                  <ul>{r.overlaps.map((o: any) => <li key={o.id}>{OTA_LABEL[o.source_channel] || o.source_channel} {stay(o.checkin_date, o.checkout_date)} {o.guest_name}
                    {o.source_channel !== 'direct' && <FeedState v={o.feed_state} s={s} />}</li>)}</ul>
                </div>
              )}
              <div className="ota-actions">
                <button type="button" className="ota-btn danger" onClick={() => run(() => api(`/review/${r.id}/confirm-cancel`, { method: 'POST', body: JSON.stringify({ force: !!r.in_feed }) }),
                    r.in_feed ? `⚠ ${s.liveWarn}` : s.confirmCancel)}>{s.cancel}</button>
                <button type="button" className="ota-btn" onClick={() => run(() => api(`/review/${r.id}/keep`, { method: 'POST' }))}>{s.keep}</button>
              </div>
            </div>
          ))}

          {tab === 'blocks' && <div className="ota-sub" style={{ marginBottom: 4 }}>{s.blockLead}</div>}
          {tab === 'blocks' && items.map((b) => <BlockCard key={b.id} b={b} s={s} run={run} />)}

          {tab === 'pending' && items.map((p) => (
            <div key={p.id} className="ota-card warn">
              <div className="ota-card-head"><b>{p.property_name}</b> <span className="ota-chip">{OTA_LABEL[p.source_channel] || p.source_channel}</span></div>
              <div className="ota-compare">
                <div><div className="ota-sub">{s.newEvent}</div><div className="ota-big">{stay(p.checkin_date, p.checkout_date)}</div>
                  {p.ota_reservation_code && <div className="ota-sub">{s.code}: {p.ota_reservation_code}</div>}</div>
                <div><div className="ota-sub">{s.candidate}</div>
                  {p.candidate_reservation_id ? <>
                    <div className="ota-big">{stay(p.candidate_checkin_date, p.candidate_checkout_date)}</div>
                    <div>{p.candidate_guest_name || s.unset} / {p.candidate_guest_count || '-'}{s.persons}</div>
                  </> : '-'}</div>
              </div>
              <div className="ota-actions">
                {p.candidate_reservation_id && <button type="button" className="ota-btn primary" onClick={() => run(() => api(`/pending/${p.id}/resolve`, { method: 'POST', body: JSON.stringify({ action: 'MERGE' }) }))}>{s.merge}</button>}
                <button type="button" className="ota-btn" onClick={() => run(() => api(`/pending/${p.id}/resolve`, { method: 'POST', body: JSON.stringify({ action: 'CREATE' }) }))}>{s.create}</button>
                <button type="button" className="ota-btn ghost" onClick={() => run(() => api(`/pending/${p.id}/resolve`, { method: 'POST', body: JSON.stringify({ action: 'IGNORE' }) }))}>{s.ignore}</button>
              </div>
            </div>
          ))}

          {tab === 'dates' && items.map((r) => (
            <div key={r.id} className="ota-card">
              <CardHead r={r} s={s} onHistory={openHistory} />
              <div className="ota-compare">
                <div><div className="ota-sub">{s.before}</div><div className="ota-big strike">{stay(r.prev_checkin_date, r.prev_checkout_date)}</div></div>
                <div><div className="ota-sub">{s.after}</div><div className="ota-big">{stay(r.checkin_date, r.checkout_date)}</div></div>
              </div>
              <div className="ota-actions">
                <button type="button" className="ota-btn primary" onClick={() => run(() => api(`/date-changes/${r.id}/ack`, { method: 'POST' }))}>{s.ack}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {history && (
        <div className="ota-modal-bg" onClick={() => setHistory(null)}>
          <div className="ota-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{s.history}</h3>
            {history.items.length === 0 ? <div className="ota-empty">{s.empty}</div> : (
              <table className="ota-table"><tbody>
                {history.items.map((h) => (
                  <tr key={h.id}>
                    <td>{h.created_at}</td><td>{h.change_type}</td>
                    <td>{h.old_checkin_date ? `${stay(h.old_checkin_date, h.old_checkout_date)} → ${stay(h.new_checkin_date, h.new_checkout_date)}` : [h.old_value, h.new_value].filter(Boolean).join(' → ')}</td>
                    <td>{h.changed_by}</td>
                  </tr>
                ))}
              </tbody></table>
            )}
            <div className="ota-actions"><button type="button" className="ota-btn" onClick={() => setHistory(null)}>{s.close}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardHead({ r, s, onHistory }: { r: any; s: Record<string, string>; onHistory: (id: string) => void }) {
  return (
    <div className="ota-card-head">
      <b>{r.property_name}</b>
      <span className="ota-chip">{OTA_LABEL[r.source_channel] || r.source_channel}</span>
      <span className="ota-big">{stay(r.checkin_date, r.checkout_date)}</span>
      {r.guest_name ? <span>{r.guest_name}{r.guest_count ? ` / ${r.guest_count}${s.persons}` : ''}</span> : <span className="ota-sub">{s.unset}</span>}
      <button type="button" className="ota-link" onClick={() => onHistory(r.id)}>{s.history}</button>
    </div>
  );
}

function GuestForm({ r, s, onSaved, onHistory }: { r: any; s: Record<string, string>; onSaved: () => void; onHistory: (id: string) => void }) {
  const [f, setF] = useState({
    guest_name: r.guest_name === 'Airbnb Guest' || r.guest_name === 'OTA Guest' ? '' : (r.guest_name || ''),
    guest_count: r.guest_count ? String(r.guest_count) : '',
    guest_phone: r.guest_phone || '', guest_email: r.guest_email || '', ota_reservation_code: r.ota_reservation_code || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api(`/reservations/${r.id}/guest`, { method: 'PUT', body: JSON.stringify({ ...f, guest_count: Number(f.guest_count) || 0 }) });
      window.dispatchEvent(new Event('ota-review-changed'));
      onSaved();
    } catch (e: any) { setErr(`${s.error}: ${e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="ota-card">
      <CardHead r={r} s={s} onHistory={onHistory} />
      <div className="ota-form">
        <label>{s.guest}<input value={f.guest_name} onChange={set('guest_name')} placeholder="山田 太郎" /></label>
        <label>{s.count}<input type="number" min={1} value={f.guest_count} onChange={set('guest_count')} /></label>
        <label>{s.phone}<input value={f.guest_phone} onChange={set('guest_phone')} /></label>
        <label>{s.email}<input type="email" value={f.guest_email} onChange={set('guest_email')} /></label>
        <label>{s.code}<input value={f.ota_reservation_code} onChange={set('ota_reservation_code')} /></label>
      </div>
      {err && <div className="ota-msg">{err}</div>}
      <div className="ota-actions">
        <button type="button" className="ota-btn primary" disabled={busy} onClick={() => void save()}>{s.save}</button>
      </div>
    </div>
  );
}

function BlockCard({ b, s, run }: { b: any; s: Record<string, string>; run: (fn: () => Promise<any>, c?: string) => Promise<void> }) {
  const [channel, setChannel] = useState('booking');
  const resolve = (body: any, confirmText?: string) =>
    run(() => api(`/pending/${b.id}/resolve`, { method: 'POST', body: JSON.stringify(body) }), confirmText);
  return (
    <div className="ota-card warn">
      <div className="ota-card-head">
        <b>{b.property_name}</b>
        <span className="ota-chip">Airbnb</span>
        <span className="ota-big">{stay(b.checkin_date, b.checkout_date)}</span>
        <span className="ota-sub">{b.nights}{s.nights} / {b.ical_summary}</span>
      </div>
      {b.overlaps?.length > 0 && (
        <div className="ota-row"><b>{s.overlapRes}:</b>
          <ul>{b.overlaps.map((o: any) => (
            <li key={o.id}>
              {OTA_LABEL[o.source_channel] || o.source_channel} {stay(o.checkin_date, o.checkout_date)} {o.guest_name}
              <button type="button" className="ota-btn" style={{ marginLeft: 8, padding: '2px 8px' }}
                onClick={() => resolve({ action: 'MERGE', reservation_id: o.id })}>{s.sameAs}</button>
            </li>
          ))}</ul>
        </div>
      )}
      <div className="ota-actions">
        <label className="ota-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{s.channel}
          <select value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option value="booking">Booking.com</option>
            <option value="airbnb">Airbnb</option>
            <option value="other">その他 / Other</option>
          </select>
        </label>
        <button type="button" className="ota-btn primary" onClick={() => resolve({ action: 'CREATE', channel })}>{s.isReservation}</button>
        <button type="button" className="ota-btn" onClick={() => resolve({ action: 'CONFIRM_BLOCK' }, s.confirmBlock)}>{s.isBlock}</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// キャンセル検知カード（/cancel-suspicions）
// ------------------------------------------------------------
function CancelCard({ x, s, run, onHistory }: {
  x: any; s: Record<string, string>;
  run: (fn: () => Promise<any>, confirmText?: string) => Promise<void>;
  onHistory: (id: string) => void;
}) {
  const ev = x.evidence || {};
  const level = x.rule === 'ACTIVE_IN_FEED' ? 'restore' : x.confidence === 'HIGH' ? 'danger' : x.confidence === 'MEDIUM' ? 'warn' : '';
  const r = { id: x.reservation_id, property_name: x.property_name, source_channel: x.channel,
    checkin_date: x.checkin_date, checkout_date: x.checkout_date, guest_name: x.guest_name,
    guest_count: x.guest_count, ota_reservation_code: x.ota_reservation_code };
  const overl: any[] = Array.isArray(ev.overlapping) ? ev.overlapping : [];
  return (
    <div className={`ota-card ${level}`}>
      <CardHead r={r} s={s} onHistory={onHistory} />
      <div className="ota-row">
        <span className={`ota-conf ota-conf-${String(x.confidence).toLowerCase()}`}>{s.confidence}: {s[x.confidence] || x.confidence}</span>
        <b>{s.reason}:</b> {s[`R_${x.rule}`] || x.rule}
      </div>
      <div className="ota-row ota-sub">
        {s.detectedAt}: {x.opened_at || x.detected_at} ・ {s.streak}: {x.streak}{s.times}
        {ev.feed && <> ・ {s.feed}: {OTA_LABEL[ev.feed] || ev.feed}</>}
        {' ・ '}{s.uid}: {x.ota_event_uid ? <code>{String(x.ota_event_uid).slice(0, 18)}…</code> : s.noUid}
      </div>
      {(x.rule === 'DATE_MISMATCH' || x.rule === 'ACTIVE_IN_FEED') && ev.feed_event && (
        <div className="ota-row"><b>{s.feedDates}:</b> <span className="ota-big">{stay(ev.feed_event.start, ev.feed_event.end)}</span></div>
      )}
      <div className="ota-row"><b>{s.overlapEv}:</b>{' '}
        {overl.length === 0 ? s.none : (
          <ul>{overl.map((o) => <li key={o.uid}>{stay(o.start, o.end)} {o.summary || ''}</li>)}</ul>
        )}
      </div>
      {x.rule === 'ACTIVE_IN_FEED' && Array.isArray(ev.overlapping_active) && ev.overlapping_active.length > 0 && (
        <div className="ota-row"><b>{s.overlapActive}:</b>
          <ul>{ev.overlapping_active.map((o: any) => <li key={o.id}>{OTA_LABEL[o.channel] || o.channel} {stay(o.start, o.end)} {o.guest_name}</li>)}</ul>
        </div>
      )}
      {x.rule === 'ACTIVE_IN_FEED' ? (
        <div className="ota-actions">
          <button type="button" className="ota-btn primary"
            onClick={() => run(() => api(`/cancel-suspicions/${x.id}/restore`, { method: 'POST' }), s.confirmRestore)}>{s.restore}</button>
          <button type="button" className="ota-btn"
            onClick={() => run(() => api(`/cancel-suspicions/${x.id}/dismiss`, { method: 'POST' }), s.confirmNotRestore)}>{s.notRestore}</button>
        </div>
      ) : (
      <div className="ota-actions">
        <button type="button" className="ota-btn danger"
          onClick={() => run(() => api(`/cancel-suspicions/${x.id}/cancel`, { method: 'POST' }), s.confirmCancel)}>{s.cancel}</button>
        <button type="button" className="ota-btn"
          onClick={() => run(() => api(`/cancel-suspicions/${x.id}/dismiss`, { method: 'POST' }), s.confirmValid)}>{s.valid}</button>
      </div>
      )}
    </div>
  );
}

/** 重複している予約が OTA のフィードで有効かどうか */
function FeedState({ v, s }: { v?: string; s: Record<string, string> }) {
  const k = v === 'active' ? 'feedActive' : v === 'missing' ? 'feedMissing' : 'feedUnknown';
  return <span className={`ota-feed ota-feed-${v || 'unknown'}`}>{s[k]}</span>;
}
